import React, { useEffect, useState } from 'react';
import { TicketTemplate } from 'store/ticketTemplates';
import { getSortedList } from 'utils/stringFormat';
import { TemplateTypeList, ticketTemplateValidationSchema } from 'views/ConfigurationView/utils';
import { FormDialog, InputField, MuiBox, Typography, SelectDropdown } from '@kis-technologies/kis-ui';
import { INVALID, VALID } from 'views/ConfigurationView/constants';
import { TemplateFormProps, TemplatePayload, TicketTemplateType } from 'views/KiosksView/types';
import { getHasEmptyInput, getHasInvalidInput } from '../../../utils';
import { useKioskFormStyles } from '../styled';
import { useTemplateStyles } from './styled';

export const TemplateForm: React.FC<TemplateFormProps> = (props: TemplateFormProps) => {
  const { mode, initialData, onClose, onSubmit, error } = props;

  const isEdit = mode === 'edit';

  const [formData, setFormData] = useState<TicketTemplate>({
    id: '',
    name: '',
    type: '',
    template: ''
  } as TicketTemplate);

  const [validation, setValidation] = useState<{ [x: string]: string }>({});
  const [validationMessage, setValidationMessage] = useState<{ [x: string]: string | null }>({});
  const [selectedType, setSelectedType] = useState<TicketTemplateType | null>(null);
  const isFormValid =
    !getHasEmptyInput({
      name: formData.name.trim(),
      type: formData.type.trim(),
      template: formData.template.trim()
    }) && !getHasInvalidInput(validation);

  const sortedTypes = getSortedList(TemplateTypeList as TicketTemplateType[], 'name');

  const formClasses = useKioskFormStyles();
  const templateClasses = useTemplateStyles();

  useEffect(() => {
    if (isEdit && initialData) {
      setFormData(initialData);

      const typeItem = sortedTypes.find((t) => t.code === initialData.type);
      setSelectedType(typeItem || null);
    }
  }, [initialData]);

  /**
   * INPUT CHANGE
   */
  const onInputChange = (key: keyof TicketTemplate, e: React.ChangeEvent<HTMLInputElement>) => {
    if (validation[key] === INVALID) {
      setValidation({ ...validation, [key]: VALID });
      setValidationMessage({ ...validationMessage, [key]: null });
    }

    setFormData({
      ...formData,
      [key]: e.target.value
    });
  };

  const onTypeChange = (item: TicketTemplateType) => {
    setSelectedType(item);

    setFormData({
      ...formData,
      type: item.code
    });
  };

  const onInputBlur = async (
    key: keyof TicketTemplate,
    e: React.ChangeEvent<HTMLInputElement>,
    value: string | null = null
  ) => {
    e.preventDefault();

    const trimmed = e.target.value.trim();

    setFormData({
      ...formData,
      [key]: trimmed
    });

    let invalidMessage = null;

    if (value && value.trim().length) {
      const result = await ticketTemplateValidationSchema.validate({ [key]: value.trim() }).catch((err) => err);

      invalidMessage = result?.message;
    }

    setValidation({
      ...validation,
      [key]: invalidMessage ? INVALID : VALID
    });

    setValidationMessage({
      ...validationMessage,
      [key]: invalidMessage
    });
  };

  const validate = (key: keyof TicketTemplate) => {
    return validationMessage[key];
  };

  const handleSubmit = () => {
    const payload: TemplatePayload = {
      name: formData.name.trim(),
      type: formData.type,
      template: formData.template.trim(),
      ...(isEdit && { id: formData.id })
    };

    onSubmit(payload);
  };

  return (
    <FormDialog
      open
      title={isEdit ? 'Edit Template' : 'Create Template'}
      confirmButtonText={isEdit ? 'Save' : 'Create'}
      cancelButtonText="Cancel"
      cancelButtonAction={onClose}
      confirmButtonAction={handleSubmit}
      confirmButtonDisabled={!isFormValid}
    >
      <MuiBox mt="14px">
        <MuiBox className={formClasses.formOverlayInputBox}>
          <InputField
            label="Name"
            size="small"
            sx={{ width: '524px', height: '45px' }}
            fullWidth
            value={formData?.name || ''}
            onChange={(e) => onInputChange('name', e)}
            onBlur={(e) => onInputBlur('name', e, e.target.value)}
            helperText={validate('name')}
            error={validation.name === INVALID}
          />
        </MuiBox>

        <MuiBox className={formClasses.formOverlayInputBox}>
          <Typography display="flex" justifyContent="space-between" className={formClasses.formDropdownLabel}>
            Type
          </Typography>

          <SelectDropdown
            placeholder="Select type"
            items={sortedTypes}
            itemToString={(item) => item?.name || ''}
            selectedItem={selectedType}
            onChange={(item: TicketTemplateType) => onTypeChange(item)}
            radius="round"
            width="262px"
            sx={{
              boxShadow: 'none',
              height: '45px',
              '& .MuiSelect-select': {
                color: selectedType ? '#000' : '#9E9E9E'
              }
            }}
          />
        </MuiBox>

        <MuiBox className={templateClasses.createTicketTemplate}>
          <InputField
            multiline
            label="Template"
            size="small"
            sx={{ width: '522px', height: '400px', borderRadius: '25px' }}
            fullWidth
            placeholder=""
            value={formData?.template || ''}
            onChange={(e) => onInputChange('template', e)}
            rows={16}
            onBlur={(e) => onInputBlur('template', e, e.target.value.trim())}
            helperText={validate('template')}
            error={validation.template === INVALID}
          />
        </MuiBox>
        {error && (
          <Typography color="red" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </MuiBox>
    </FormDialog>
  );
};
